import adapters from "../config/tracking/adapters";
import { TRACKING_PROVIDER } from "../config/tracking/providers";
import { FEATURE_TOGGLE_BLOCK_TRACKING_GA4, FEATURE_TOGGLE_BLOCK_TRACKING_NETCORE } from "../constants/features";

import { ENV, CURRENT_ENV } from "../constants/environment";

const isLocalDevMode = CURRENT_ENV === ENV.LOCAL || ENV.DEV;
const isProdMode = CURRENT_ENV === ENV.PROD;

const DEVELOPMENT_API_KEY_OVERRIDE = {
  [TRACKING_PROVIDER.META]: "616535497656998", // jasper's own account
  [TRACKING_PROVIDER.TIKTOK]: "CV01PHJC77U0CTE65Q2G", // jasper's own account
};

const isProviderEnabled = (providerName) => {
  try {
    if (!adapters[providerName]) {
      console.error("[TrackingUtil] isProviderEnabled: providerName is not exported!", providerName);

      return false;
    }
    // console.debug("[TrackingUtil] isProviderEnabled", {
    //   providerName,
    // });

    switch (providerName) {
      // case TRACKING_PROVIDER.META:
      //   return FEATURE_TOGGLE_BLOCK_PIXEL_META === false;
      // case TRACKING_PROVIDER.TIKTOK:
      //   return FEATURE_TOGGLE_BLOCK_PIXEL_TIKTOK === false;
      case TRACKING_PROVIDER.GTM:
        return FEATURE_TOGGLE_BLOCK_TRACKING_GA4 === false;
      case TRACKING_PROVIDER.NETCORE:
        return FEATURE_TOGGLE_BLOCK_TRACKING_NETCORE === false;
      default:
        return false;
    }
  } catch (e) {
    console.error("[TrackingUtil] isProviderEnabled: Exception caught:", e);
  }
};

const getEffectiveApiKey = (providerName, providerApiKey) => {
  try {
    return !isProdMode ? (DEVELOPMENT_API_KEY_OVERRIDE[providerName] ?? null) : providerApiKey;
  } catch (e) {
    console.error("[TrackingUtil] getEffectiveApiKey: Exception caught:", e);
  }

  return null;
};

const registerApiKeyForProvider = (providerName, providerApiKey) => {
  try {
    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    if (!adapter.isScriptExists()) {
      return;
    }
    console.debug("[TrackingUtil] registerApiKeyForProvider", {
      providerName,
      providerApiKey,
    });
    const effectiveApiKey = getEffectiveApiKey(providerName, providerApiKey);
    if (adapter.isInstanceExists(effectiveApiKey)) {
      console.warn("[TrackingUtil] registerApiKeyForProvider: effectiveApiKey already registered. Skipping. ", providerName);
    } else if (effectiveApiKey) {
      adapter.register(effectiveApiKey);
    }
  } catch (e) {
    console.error("[TrackingUtil] registerApiKeyForProvider: Exception caught:", e);
  }
};

const initConfigForProvider = (providerName) => {
  try {
    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    if (!adapter.isScriptExists()) {
      return;
    }
    console.debug("[TrackingUtil] initConfigForProvider", {
      providerName,
    });
    if (isLocalDevMode) {
      adapter.activateDebugMode?.();
    }
    adapter.grantConsent?.();
  } catch (e) {
    console.error("[TrackingUtil] initConfigForProvider: Exception caught:", e);
  }
};

const sendPageViewForProvider = (providerName, specificProviderApiKey) => {
  try {
    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    if (!adapter.isScriptExists() || !adapter.hasInstance()) {
      return;
    }
    console.debug("[TrackingUtil] sendPageViewForProvider", {
      providerName,
      specificProviderApiKey,
    });
    if (specificProviderApiKey) {
      const effectiveApiKey = getEffectiveApiKey(providerName, specificProviderApiKey);
      if (!adapter.isInstanceExists(effectiveApiKey)) {
        console.warn("[TrackingUtil] sendPageViewForProvider: effectiveApiKey not found for ", providerName);

        return;
      }
      if (effectiveApiKey) {
        adapter.pageViewSingle(effectiveApiKey);
      }
    } else {
      adapter.pageView();
    }
  } catch (e) {
    console.error("[TrackingUtil] sendPageViewForProvider: Exception caught:", e);
  }
};

const sendEventForProvider = (providerName, eventName, eventData = {}, specificProviderApiKey) => {
  try {
    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    if (!adapter.isScriptExists() || !adapter.hasInstance()) {
      return;
    }
    console.debug("[TrackingUtil] sendEventForProvider", {
      providerName,
      eventName,
      eventData,
      specificProviderApiKey,
    });
    if (specificProviderApiKey) {
      const effectiveApiKey = getEffectiveApiKey(providerName, specificProviderApiKey);
      if (!adapter.isInstanceExists(effectiveApiKey)) {
        console.warn("[TrackingUtil] sendEventForProvider: effectiveApiKey not found for ", providerName);

        return;
      } if (eventName) {
        adapter.trackSingle(effectiveApiKey, eventName, eventData);
      }
    } else if (eventName) {
      adapter.track(eventName, eventData);
    }
  } catch (e) {
    console.error("[TrackingUtil] sendEventForProvider: Exception caught:", e);
  }
};

const stopTrackingForProvider = (providerName) => {
  try {
    // Clear User
    clearUserForProvider(providerName);
    // End Clear User

    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    if (!adapter.isScriptExists() || !adapter.hasInstance()) {
      return;
    }
    console.debug("[TrackingUtil] stopTrackingForProvider", {
      providerName,
    });
    adapter.revokeConsent?.();
  } catch (e) {
    console.error("[TrackingUtil] stopTrackingForProvider: Exception caught:", e);
  }
};

const pushUserDataForProvider = (providerName, userPayload) => {
  try {
    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    if (!adapter.isScriptExists() || !adapter.hasInstance()) {
      return;
    }
    console.debug("[TrackingUtil] pushUserDataForProvider", {
      providerName,
      userPayload,
    });
    adapter.appendUserAttributes?.(userPayload);
  } catch (e) {
    console.error("[TrackingUtil] pushUserDataForProvider: Exception caught:", e);
  }
};

const identifyUserForProvider = (providerName, userIdentifier) => {
  try {
    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    console.info("[TrackingUtil] identifyUserForProvider", {
      isScriptExists: adapter.isScriptExists(),
      hasInstance: adapter.hasInstance(),
    });
    if (!adapter.isScriptExists() || !adapter.hasInstance()) {
      return;
    }
    console.info("[TrackingUtil] identifyUserForProvider", {
      providerName,
      userIdentifier,
    });
    adapter.identifyUser?.(userIdentifier);
  } catch (e) {
    console.error("[TrackingUtil] identifyUserForProvider: Exception caught:", e);
  }
};

/**
 * Runs a callback once the provider instance is ready.
 * Polls until the provider SDK is loaded (and optionally hasInstance).
 * Use when the provider SDK loads asynchronously (e.g. via GTM).
 *
 * @param {string} providerName - Provider (e.g. TRACKING_PROVIDER.NETCORE)
 * @param {function} callback - Function to run when ready (no args)
 * @param {Object} options - Optional config
 * @param {boolean} options.requireInstance - If true, wait for hasInstance (default: true)
 * @param {number} options.maxAttempts - Max poll attempts (default: 20)
 * @param {number} options.intervalMs - Poll interval in ms (default: 500)
 * @param {string} options.actionLabel - Label for logging (e.g. "send event", "identify user")
 * @returns {function} - Cleanup function to cancel polling (call on unmount)
 */
const runWhenProviderReady = (providerName, callback, options = {}) => {
  const { maxAttempts = 20, intervalMs = 500, requireInstance = true, actionLabel = "run" } = options;

  if (isProviderEnabled(providerName) === false) {
    return () => {};
  }

  const adapter = adapters[providerName];
  if (!adapter) {
    return () => {};
  }

  let attempt = 0;
  let timeoutId = null;

  const isReady = () => {
    if (!adapter.isScriptExists()) return false;
    if (requireInstance && !adapter.hasInstance()) return false;
    return true;
  };

  const tryRun = () => {
    attempt += 1;
    if (isReady()) {
      console.info("[TrackingUtil] runWhenProviderReady: instance ready", {
        providerName,
        actionLabel,
        attempt,
      });
      callback();
      return true;
    }
    if (attempt >= maxAttempts) {
      console.warn("[TrackingUtil] runWhenProviderReady: max attempts reached, instance not ready", {
        providerName,
        actionLabel,
      });
      return true; // stop polling
    }
    return false;
  };

  if (tryRun()) {
    return () => {};
  }

  timeoutId = setInterval(() => {
    if (tryRun()) {
      clearInterval(timeoutId);
      timeoutId = null;
    }
  }, intervalMs);

  return () => {
    if (timeoutId) {
      clearInterval(timeoutId);
    }
  };
};

/**
 * Identifies user for provider once the instance is loaded.
 * Use this when the provider SDK loads asynchronously (e.g. via GTM).
 *
 * @param {string} providerName - Provider to identify for (e.g. TRACKING_PROVIDER.NETCORE)
 * @param {string} userIdentifier - User identifier (e.g. externalCustomerId)
 * @param {Object} options - Optional: { maxAttempts, intervalMs }
 * @returns {function} - Cleanup function to cancel polling (call on unmount)
 */
const identifyUserForProviderWhenReady = (providerName, userIdentifier, options = {}) => {
  if (!userIdentifier) return () => {};
  return runWhenProviderReady(
    providerName,
    () => identifyUserForProvider(providerName, userIdentifier),
    { ...options, actionLabel: "identify user" }
  );
};

const clearUserForProvider = (providerName) => {
  try {
    if (isProviderEnabled(providerName) === false) {
      return;
    }

    const adapter = adapters[providerName];
    if (!adapter.isScriptExists() || !adapter.hasInstance()) {
      return;
    }
    console.debug("[TrackingUtil] clearUserForProvider", {
      providerName,
    });
    adapter.clearUser?.();
  } catch (e) {
    console.error("[TrackingUtil] clearUserForProvider: Exception caught:", e);
  }
};

// const sendPixelPageViewHelper = () => {
//   sendPageViewForProvider(TRACKING_PROVIDER.META);
//   sendPageViewForProvider(TRACKING_PROVIDER.TIKTOK);
// };

const sendPixelEventHelper = (eventName, eventData = {}) => {
  sendEventForProvider(TRACKING_PROVIDER.META, eventName, eventData);
  sendEventForProvider(TRACKING_PROVIDER.TIKTOK, eventName, eventData);
};

const stopPixelTrackingHelper = () => {
  stopTrackingForProvider(TRACKING_PROVIDER.META);
  stopTrackingForProvider(TRACKING_PROVIDER.TIKTOK);
};

/**
 * Sends event for provider once the instance is loaded.
 * Use when the provider SDK loads asynchronously (e.g. via GTM).
 *
 * @param {string} providerName - Provider (e.g. TRACKING_PROVIDER.NETCORE)
 * @param {string} eventName - Event name
 * @param {Object} eventData - Event payload
 * @param {Object} options - Optional: { maxAttempts, intervalMs }
 * @returns {function} - Cleanup to cancel polling (call on unmount)
 */
const sendEventForProviderWhenReady = (providerName, eventName, eventData = {}, options = {}) => {
  if (!eventName) return () => {};
  return runWhenProviderReady(
    providerName,
    () => sendEventForProvider(providerName, eventName, eventData),
    { ...options, actionLabel: "send event" }
  );
};

/**
 * Initializes config for provider once the instance is loaded.
 * Use when the provider SDK loads asynchronously (e.g. via GTM).
 *
 * @param {string} providerName - Provider (e.g. TRACKING_PROVIDER.NETCORE)
 * @param {Object} options - Optional: { maxAttempts, intervalMs, requireInstance }
 * @returns {function} - Cleanup to cancel polling (call on unmount)
 */
const initConfigForProviderWhenReady = (providerName, options = {}) => {
  return runWhenProviderReady(
    providerName,
    () => initConfigForProvider(providerName),
    { ...options, requireInstance: false, actionLabel: "init config" }
  );
};

/**
 * Sends page view for provider once the instance is loaded.
 * Use when the provider SDK loads asynchronously (e.g. via GTM).
 *
 * @param {string} providerName - Provider (e.g. TRACKING_PROVIDER.NETCORE)
 * @param {Object} options - Optional: { specificProviderApiKey, maxAttempts, intervalMs }
 * @returns {function} - Cleanup to cancel polling (call on unmount)
 */
const sendPageViewForProviderWhenReady = (providerName, options = {}) => {
  const { specificProviderApiKey, ...runOptions } = options;
  return runWhenProviderReady(
    providerName,
    () => sendPageViewForProvider(providerName, specificProviderApiKey),
    { ...runOptions, actionLabel: "send page view" }
  );
};

const sendNetcoreEventHelper = (eventName, eventData = {}) => {
  sendEventForProvider(TRACKING_PROVIDER.NETCORE, eventName, eventData);
};

const sendNetcoreEventHelperWhenReady = (eventName, eventData = {}, options = {}) => {
  return sendEventForProviderWhenReady(
    TRACKING_PROVIDER.NETCORE,
    eventName,
    eventData,
    options
  );
};

const clearNetcoreUserHelper = () => {
  clearUserForProvider(TRACKING_PROVIDER.NETCORE);
};

const TrackingUtil = {
  isProviderEnabled,
  getEffectiveApiKey,
  registerApiKeyForProvider,
  initConfigForProvider,
  initConfigForProviderWhenReady,
  sendPageViewForProvider,
  sendPageViewForProviderWhenReady,
  sendEventForProvider,
  sendEventForProviderWhenReady,
  stopTrackingForProvider,
  pushUserDataForProvider,
  identifyUserForProvider,
  identifyUserForProviderWhenReady,
  clearUserForProvider,
  runWhenProviderReady,
  // sendPixelPageViewHelper,
  sendPixelEventHelper,
  stopPixelTrackingHelper,
  sendNetcoreEventHelper,
  sendNetcoreEventHelperWhenReady,
  clearNetcoreUserHelper,
};

export default TrackingUtil;
